/**
 * ScenarioPlannerSkeleton
 *
 * A skeleton loading state for the ScenarioPlanner component
 */

import React from 'react'
import { cn } from '@/lib/utils'

interface ScenarioPlannerSkeletonProps {
  className?: string
}

export const ScenarioPlannerSkeleton: React.FC<ScenarioPlannerSkeletonProps> = ({ className }) => {
  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex animate-pulse flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-36 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>

      {/* Scenario tabs */}
      <div className="border-b">
        <div className="flex gap-2 overflow-x-auto p-2">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className={`h-8 rounded-md px-4 py-1 ${i === 0 ? 'bg-primary/20' : 'bg-gray-200 dark:bg-gray-700'}`}
              style={{ width: `${70 + i * 20}px` }}
            ></div>
          ))}
        </div>
      </div>

      {/* Scenario content */}
      <div className="p-4">
        {/* Scenario title */}
        <div className="mb-4">
          <div className="mb-2 h-5 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>

        {/* Scenario details */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Left column */}
          <div className="space-y-4">
            <div>
              <div className="mb-2 h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-4/5 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>

            <div>
              <div className="mb-2 h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-4">
            <div>
              <div className="mb-2 h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-4/5 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>

            <div>
              <div className="mb-2 h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Probability indicator */}
        <div className="mt-6 border-t pt-4">
          <div className="mb-2 h-4 w-1/5 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <div className="h-2 w-3/5 rounded-full bg-blue-400 dark:bg-blue-600"></div>
          </div>
        </div>
      </div>
    </div>
  )
}
